import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import { useNavigate } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { idPisoGrilla, type FotoCarrusel, type SegmentoBarra } from "@/components";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, API, type Adjunto, type EstadoUnidad, type Fideicomiso, type Maqueta, type MaquetaEjemplo, type UnidadProyecto } from "@/lib/api";
import { pisosFachada } from "@/lib/edificio";
import { maquetaEsquematica, pisosDe, puedeArmarEjemplo, resumenEjemplo, TOKEN_ESTADO } from "@/lib/maqueta";
import {
  coincide,
  disponibilidad,
  esNewbery3563,
  hayFiltro,
  hayLados,
  opcionesAmbientes,
  ordenFicha,
  pisosAnexos,
  pisosGrilla,
  resumenPorTipo,
  SIN_FILTRO,
  solDe,
  unidadesVisibles,
  type FiltroProyecto,
} from "@/lib/proyecto";
import { formatM2 } from "@/lib/unidades";
import { ETIQUETA_ESTADO_UNIDAD } from "@/lib/ventas";
import { NEWBERY_3563 } from "./newbery3563";

export type VistaEdificio = "plano" | "maqueta";

/** Lo que no coincide con el filtro, en el frente y la maqueta: gris. */
const TOKEN_APAGADA = "--color-borde";
const ORDEN_LEYENDA: EstadoUnidad[] = ["disponible", "reservada", "adjudicada", "vendida", "fuera_de_venta"];
const DESLIZAR_MINIMO = 60; // px de arrastre horizontal para pasar de unidad en la ficha

/** Las imágenes de la ficha: primero los renders (lo que más vende), después el plano entero. */
function fotosFicha(fid: number, u: UnidadProyecto): FotoCarrusel[] {
  const ruta = (a: Adjunto) => `${API}/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`;
  return [
    ...u.renders.map((a) => ({ id: a.id, src: ruta(a), alt: a.nombre, grupo: "Renders", ajuste: "cubrir" as const })),
    ...u.planos.map((a) => ({ id: a.id, src: ruta(a), alt: a.nombre, grupo: "Plano", ajuste: "contener" as const })),
  ];
}

function superficie(u: UnidadProyecto): SegmentoBarra[] {
  return [
    { clave: "cubierta", etiqueta: "Cubierta", valor: Number(u.sup_cubierta_m2), texto: formatM2(u.sup_cubierta_m2), token: "--color-carbon" },
    { clave: "semicubierta", etiqueta: "Semicubierta", valor: Number(u.sup_semicubierta_m2), texto: formatM2(u.sup_semicubierta_m2), token: "--color-marca" },
    { clave: "descubierta", etiqueta: "Descubierta", valor: Number(u.sup_descubierta_m2), texto: formatM2(u.sup_descubierta_m2), token: "--color-estado-disponible" },
  ];
}

/**
 * Proyecto (sin JSX; rediseño de Mati, 2026-11-02): cuántos deptos quedan, filtros (ambientes, frente o contrafrente,
 * sólo disponibles) que apagan lo que no coincide en el frente, la maqueta y la grilla por piso, y la ficha de cada
 * unidad con sus imágenes grandes, su superficie, hacia dónde mira y "Anterior"/"Siguiente" entre las que coinciden.
 * Sin precio, comprador ni a quién se adjudicó (Mati, 2026-10-20). El admin sube la maqueta (.glb) o arma la de ejemplo.
 */
export function useProyectoTab() {
  const { fid, membresia } = useContextoApp();
  const esAdmin = membresia.rol === "admin";
  const navegar = useNavigate();
  const fideicomiso = useApiResource<Fideicomiso>(`/api/fideicomisos/${fid}`);
  const unidades = useApiResource<UnidadProyecto[]>(`/api/fideicomisos/${fid}/unidades/proyecto`);
  const maqueta = useApiResource<Maqueta>(`/api/fideicomisos/${fid}/maqueta`);
  // Sin lo cargado en los pisos anexos (Newbery 3563: el depto de prueba del 8, que es la azotea del dúplex del 7)
  const anexos = useMemo(() => pisosAnexos(fideicomiso.data?.direccion ?? null), [fideicomiso.data?.direccion]);
  const lista = useMemo(() => unidadesVisibles(unidades.data ?? [], anexos), [unidades.data, anexos]);

  // Filtros
  const [filtro, setFiltro] = useState<FiltroProyecto>(SIN_FILTRO);
  const coinciden = useMemo(() => lista.filter((u) => coincide(u, filtro)).length, [lista, filtro]);
  const tokenDe = (u: UnidadProyecto) => (coincide(u, filtro) ? TOKEN_ESTADO[u.estado_venta] : TOKEN_APAGADA);

  // El edificio: el frente (tocar un piso lleva a su fila de la grilla) o la maqueta 3D
  const [vista, setVista] = useState<VistaEdificio>("plano");
  const [pisoFachada, setPisoFachada] = useState<number | null>(null);
  const fachada = useMemo(
    () => pisosFachada(lista, anexos).map((p) => ({ ...p, ventanas: p.ventanas.map((v) => ({ ...v, token: tokenDe(lista.find((u) => u.id === v.id)!) })) })),
    [lista, filtro, anexos],
  );
  const [pisoElegido, setPisoElegido] = useState<number | null>(null);
  const adjunto = maqueta.data?.adjunto ?? null;
  const esquema = useMemo(() => maquetaEsquematica(lista), [lista]);
  const enMaqueta = useMemo(
    () => lista.map((u) => ({ id: u.id, tipo: u.tipo, piso: u.piso, numero: u.numero, token: tokenDe(u) })),
    [lista, filtro],
  );

  // La ficha
  const [elegidaId, setElegidaId] = useState<number | null>(null);
  const elegida = lista.find((u) => u.id === elegidaId) ?? null;
  const orden = useMemo(() => (elegidaId === null ? [] : ordenFicha(lista, filtro, elegidaId)), [lista, filtro, elegidaId]);
  const posicion = elegidaId === null ? -1 : orden.indexOf(elegidaId);
  const mover = (paso: number) => {
    const destino = orden[posicion + paso];
    if (destino !== undefined) setElegidaId(destino);
  };
  const moverRef = useRef(mover);
  moverRef.current = mover;
  useEffect(() => {
    if (elegidaId === null) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") moverRef.current(-1);
      if (e.key === "ArrowRight") moverRef.current(1);
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [elegidaId]);
  const inicioToque = useRef<{ x: number; y: number } | null>(null);

  const accionSubir = useAsyncAction((archivo: File) => {
    const f = new FormData();
    f.append("archivo", archivo);
    return api.upload<Adjunto>(`/api/fideicomisos/${fid}/maqueta/adjuntos`, f);
  });
  const accionBorrar = useAsyncAction((id: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${id}`).then(() => true));
  const accionEjemplo = useAsyncAction(() => api.post<MaquetaEjemplo>(`/api/fideicomisos/${fid}/maqueta/ejemplo`));
  const [avisoEjemplo, setAvisoEjemplo] = useState<string | null>(null);

  // La presentación comercial (Mati, 2026-11-03): sólo en Newbery 3563.
  const presentacion = esNewbery3563(fideicomiso.data?.direccion ?? null) ? NEWBERY_3563 : null;
  const [planta, setPlanta] = useState("135");
  const [espacio, setEspacio] = useState(0);
  const [avisoCompartir, setAvisoCompartir] = useState<string | null>(null);
  const resumen = disponibilidad(lista);

  return {
    nombre: fideicomiso.data?.nombre ?? null,
    direccion: fideicomiso.data?.direccion ?? null,
    cargando: !unidades.data && unidades.loading,
    error: !unidades.data && unidades.error ? "No se pudieron cargar las unidades" : null,
    reintentar: unidades.recargar,
    hayUnidades: lista.length > 0,
    // Arriba: cuántos quedan, y el resto de los tipos en una línea
    disponibilidad: resumen,
    // La presentación: portada, relato con los renders, plantas y cierre
    presentacion,
    disponiblesPortada: lista.length > 0 ? { cantidad: resumen.disponibles, detalle: resumen.detalle } : null,
    planta,
    elegirPlanta: setPlanta,
    espacio,
    elegirEspacio: setEspacio,
    avisoCompartir,
    /** El link a la carpeta comercial: el menú de compartir del celular o, en la compu, copiado. */
    compartir: async () => {
      if (!presentacion) return;
      const url = new URL(presentacion.carpeta.url, window.location.origin).href;
      const titulo = `${presentacion.portada.nombre} ${presentacion.portada.numero}`;
      setAvisoCompartir(null);
      if (navigator.share) {
        try {
          await navigator.share({ title: titulo, text: `${titulo}: ${presentacion.portada.bajada} Mirá la carpeta comercial.`, url });
        } catch {
          // Cerró el menú de compartir: nada que avisar.
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        setAvisoCompartir("Copiamos el link de la carpeta comercial: pegalo donde quieras.");
      } catch {
        setAvisoCompartir(`No pudimos copiar el link. Es este: ${url}`);
      }
    },
    otrosTipos: resumenPorTipo(lista).filter((r) => r.tipo !== "departamento" && lista.some((u) => u.tipo === "departamento")),
    // Filtros
    filtro,
    opcionesAmbientes: opcionesAmbientes(lista),
    hayLados: hayLados(lista),
    elegirAmbientes: (ambientes: number | null) => setFiltro((f) => ({ ...f, ambientes })),
    elegirLado: (lado: FiltroProyecto["lado"]) => setFiltro((f) => ({ ...f, lado })),
    elegirSoloDisponibles: (soloDisponibles: boolean) => setFiltro((f) => ({ ...f, soloDisponibles })),
    hayFiltro: hayFiltro(filtro),
    limpiarFiltro: () => setFiltro(SIN_FILTRO),
    coinciden,
    // La grilla por piso
    grilla: pisosGrilla(lista, filtro),
    // El edificio
    leyenda: [
      ...ORDEN_LEYENDA.filter((e) => lista.some((u) => u.estado_venta === e)).map((e) => ({ clave: e, etiqueta: ETIQUETA_ESTADO_UNIDAD[e], token: TOKEN_ESTADO[e] })),
      ...(hayFiltro(filtro) ? [{ clave: "apagada", etiqueta: "No coincide", token: TOKEN_APAGADA }] : []),
    ],
    vista,
    elegirVista: setVista,
    fachada,
    pisoFachada,
    irAPiso: (piso: number) => {
      setPisoFachada(piso);
      document.getElementById(idPisoGrilla(piso))?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    },
    modeloUrl: adjunto ? `${API}/api/fideicomisos/${fid}/maqueta/archivo?v=${adjunto.id}` : null,
    esquematica: !adjunto,
    esquema,
    enMaqueta,
    pisos: pisosDe(lista),
    pisoElegido,
    elegirPiso: setPisoElegido,
    // La ficha
    elegida,
    elegir: setElegidaId,
    cerrarFicha: () => setElegidaId(null),
    posicionFicha: posicion < 0 ? "" : `${posicion + 1} de ${orden.length}`,
    hayAnterior: posicion > 0,
    haySiguiente: posicion >= 0 && posicion < orden.length - 1,
    anterior: () => mover(-1),
    siguiente: () => mover(1),
    // Deslizar de costado en la ficha pasa de unidad (salvo sobre las imágenes, que deslizan entre ellas).
    alTocarFicha: (e: TouchEvent) => {
      const t = e.touches[0];
      const enCarrusel = (e.target as HTMLElement).closest?.("[data-carrusel]");
      inicioToque.current = t && !enCarrusel ? { x: t.clientX, y: t.clientY } : null;
    },
    alSoltarFicha: (e: TouchEvent) => {
      const t = e.changedTouches[0];
      const inicio = inicioToque.current;
      inicioToque.current = null;
      if (!t || !inicio) return;
      const dx = t.clientX - inicio.x;
      if (Math.abs(dx) >= DESLIZAR_MINIMO && Math.abs(dx) > Math.abs(t.clientY - inicio.y) * 1.5) mover(dx < 0 ? 1 : -1);
    },
    fotos: elegida ? fotosFicha(fid, elegida) : [],
    tokenElegida: elegida ? TOKEN_ESTADO[elegida.estado_venta] : null,
    superficie: elegida && Number(elegida.sup_total_m2) > 0 ? superficie(elegida) : null,
    sol: elegida ? solDe(elegida.mira_a) : null,
    // Recorrido inmersivo (Mati, 2026-11-01): deptos, locales y oficinas.
    tieneRecorrido: elegida !== null && ["departamento", "local", "oficina"].includes(elegida.tipo),
    recorrer: () => elegida && navegar(`/proyecto/recorrido/${elegida.id}`),
    // Archivo de la maqueta (sólo el admin)
    esAdmin,
    archivoMaqueta: adjunto ? [adjunto] : [],
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subirMaqueta: async (archivo: File) => {
      accionBorrar.limpiarError();
      if (await accionSubir.run(archivo)) maqueta.recargar();
    },
    borrarMaqueta: async (a: Adjunto) => {
      accionSubir.limpiarError();
      if (await accionBorrar.run(a.id)) maqueta.recargar();
    },
    subiendoMaqueta: accionSubir.loading,
    errorMaqueta: accionSubir.error ?? accionBorrar.error,
    // Maqueta de ejemplo: inventa plantas con las unidades y sube la maqueta, los planos y las vistas desde arriba.
    puedeArmarEjemplo: esAdmin && puedeArmarEjemplo(adjunto),
    rehaceEjemplo: adjunto !== null,
    armarEjemplo: async () => {
      setAvisoEjemplo(null);
      const r = await accionEjemplo.run();
      if (!r) return;
      setAvisoEjemplo(resumenEjemplo(r));
      maqueta.recargar();
      unidades.recargar();
    },
    armandoEjemplo: accionEjemplo.loading,
    avisoEjemplo,
    errorEjemplo: accionEjemplo.error,
  };
}
